import { gql, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { errorFragment } from "@/gql/GqlErrorFragment";
import {
  UpdateExpectedVolumesInput,
  UpdateExpectedVolumesMutation,
  UpdateExpectedVolumesMutationVariables,
} from "@/gql/graphql";
import { expectedVolumeFetcherQuery } from "./UseExpectedVolume";

const mutation = gql`
  ${errorFragment}
  mutation UpdateExpectedVolumes($input: UpdateExpectedVolumesInput!) {
    productPrice {
      expectedVolume {
        updateExpectedVolumes(input: $input) {
          ... on UpdateExpectedVolumesSuccessResult {
            volumes {
              id
              expectedVolume
              volume12Months
            }
          }
          ... on ErrorResult {
            ...ErrorFragment
          }
        }
      }
    }
  }
`;

export const useUpdateExpectedVolumesMutation = () => {
  const [mutate, { error, loading }] = useMutation<
    UpdateExpectedVolumesMutation,
    UpdateExpectedVolumesMutationVariables
  >(mutation, {
    refetchQueries: [expectedVolumeFetcherQuery, "ExpectedVolumeFetcher"],
  });

  const [requestErrors, setRequestErrors] = useState<string[]>([]);

  const updateExpectedVolumes = useCallback(
    async (input: UpdateExpectedVolumesInput) => {
      setRequestErrors([]);

      const response = await mutate({
        variables: {
          input,
        },
      });

      const updateResult =
        response.data?.productPrice.expectedVolume.updateExpectedVolumes;

      const errors = [
        ...(response.errors?.map((e) => e.message) ?? []),
        ...((updateResult &&
          "errors" in updateResult &&
          updateResult.errors.map((error) => error.message)) ||
          []),
      ];

      setRequestErrors(errors);

      return updateResult;
    },
    [mutate]
  );

  return {
    updateExpectedVolumes,
    errors: error ? [error.message] : requestErrors,
    loading,
  };
};
